"use client";

import Image from "next/image";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { buildAppUrl } from "../config/get-config";
import HomeAuthCta from "./HomeAuthCta";
import type { AppConfig } from "../config/get-config";

type HomeHeroCarouselProps = {
	runtimeConfig: AppConfig;
};

const heroSlides = [
	{
		id: "dashboard",
		alt: "PTE Ultra practice dashboard",
		desktopSrc:
			"/assets/home-page-10/carousel/app-dashabord-desktop.webp",
		mobileSrc: "/assets/home-page-10/carousel/app-dashabord-mobile.webp",
		objectFit: "contain",
	},
	{
		id: "report",
		alt: "PTE Ultra score report preview",
		desktopSrc: "/assets/home-page-10/carousel/carousel-report.webp",
		mobileSrc: "/assets/home-page-10/carousel/carousel-report-mobile.webp",
		objectFit: "contain-mobile",
	},
	{
		id: "student",
		alt: "Student practicing with PTE Ultra",
		desktopSrc: "/assets/home-page-10/carousel/carousel-student.webp",
		mobileSrc: "/assets/home-page-10/carousel/carousel-student-mobile.webp",
		objectFit: "contain-mobile",
	},
] as const;

function getHeroImageClass(objectFit: (typeof heroSlides)[number]["objectFit"]) {
	if (objectFit === "contain") return "object-contain";
	if (objectFit === "contain-mobile") return "object-contain md:object-cover";
	return "object-cover";
}

export default function HomeHeroCarousel({
	runtimeConfig,
}: HomeHeroCarouselProps) {
	const [activeSlide, setActiveSlide] = useState(0);
	const [emblaRef, emblaApi] = useEmblaCarousel(
		{ align: "start", loop: true },
		[
			Autoplay({
				delay: 4500,
				stopOnInteraction: false,
				stopOnMouseEnter: true,
			}),
		],
	);
	const signupHref = useMemo(
		() => buildAppUrl("/signup", runtimeConfig),
		[runtimeConfig],
	);

	const updateActiveSlide = useCallback(() => {
		if (!emblaApi) return;
		setActiveSlide(emblaApi.selectedScrollSnap());
	}, [emblaApi]);

	useEffect(() => {
		if (!emblaApi) return;

		updateActiveSlide();
		emblaApi.on("select", updateActiveSlide);
		emblaApi.on("reInit", updateActiveSlide);

		return () => {
			emblaApi.off("select", updateActiveSlide);
			emblaApi.off("reInit", updateActiveSlide);
		};
	}, [emblaApi, updateActiveSlide]);

	const showPreviousSlide = () => {
		emblaApi?.scrollPrev();
	};

	const showNextSlide = () => {
		emblaApi?.scrollNext();
	};

	return (
		<section
			aria-label="Hero section"
			className="relative overflow-hidden bg-[#071325] text-white"
		>
			<div aria-hidden className="pointer-events-none absolute inset-0">
				<div className="absolute inset-0 bg-[linear-gradient(180deg,#07204a_0%,#071737_44%,#071325_100%)]" />
				<div className="absolute inset-0 opacity-40 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-size-[44px_44px]" />
				<div className="absolute right-[7%] top-[10%] h-14 w-24 bg-[radial-gradient(circle,#f23886_10%,transparent_11%)] [background-size:18px_18px] opacity-45" />
				<div className="absolute left-[4%] top-[38%] h-16 w-12 bg-[radial-gradient(circle,#f23886_10%,transparent_11%)] [background-size:18px_18px] opacity-30" />
				<div className="absolute right-[4%] top-[54%] h-14 w-10 rounded-full border border-white/15 opacity-40" />
			</div>

			<div className="relative mx-auto max-w-[1280px] px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-18">
				<div className="relative">
					<div className="mb-6 text-center sm:mb-8 lg:mb-10">
						{/* <h1 className="text-balance text-[2rem] font-semibold tracking-tight text-white sm:text-[2.8rem] lg:text-[3.5rem]">
							Increase your score.
						</h1> */}
						<h1 className="text-balance text-[2rem] font-semibold tracking-tight text-white sm:text-[2.8rem] lg:text-[3.5rem]">
							<span className="text-[#fff]">
								Master every PTE question type.
							</span>
						</h1>
						<p className="mt-3 text-[0.95rem] font-medium text-white/65 sm:text-[1.05rem] lg:text-[1.1rem]">
							Prepare for PTE Academic and PTE Core across Speaking,
							Writing, Reading and Listening with realistic practice,
							AI-powered feedback and section mock tests.

						</p>
						<div className="mt-6 flex justify-center">
							<HomeAuthCta
								runtimeConfig={runtimeConfig}
								primaryLabel="Start Practising"
								primaryAriaLabel="Get AI scoring feedback from instant AI scoring section"
								primaryClassName="inline-flex items-center justify-center gap-2 whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive has-[>svg]:px-3 h-auto min-h-12 rounded-full bg-secondary px-7 py-3 text-base font-semibold text-secondary-foreground shadow-[0_14px_34px_rgba(192,0,99,0.24)] hover:bg-secondary/90"
								containerClassName="flex items-center justify-center"
							/>
						</div>
					</div>

					<div className="w-full">
						<div
							ref={emblaRef}
							className="relative h-[520px] w-full overflow-hidden rounded-[15px] sm:h-[560px] md:h-[620px] lg:h-[720px]"
						>
							<div className="flex h-full touch-pan-y">
								{heroSlides.map((slide, index) => (
									<Link
										key={slide.id}
										href={signupHref}
										aria-label="Sign up"
										className="relative block h-full min-w-0 flex-[0_0_100%] overflow-hidden"
									>
										<picture>
											<source
												media="(max-width: 767px)"
												srcSet={slide.mobileSrc}
											/>
											<Image
												src={slide.desktopSrc}
												alt={slide.alt}
												fill
												priority={index === 0}
												sizes="(min-width: 1280px) 1240px, calc(100vw - 32px)"
												className={getHeroImageClass(
													slide.objectFit,
												)}
											/>
										</picture>
										<div className="absolute inset-0 bg-[radial-gradient(780px_420px_at_50%_0%,rgba(255,255,255,0.14),transparent_60%)]" />
									</Link>
								))}
							</div>
						</div>

						<div className="pointer-events-none relative -mt-[72px] flex items-center justify-between px-3 pb-4 sm:px-5">
							<button
								type="button"
								aria-label="Show previous hero slide"
								onClick={showPreviousSlide}
								className="pointer-events-auto grid size-11 place-items-center rounded-full border border-white/18 bg-[#071325]/76 text-white shadow-[0_12px_28px_rgba(0,0,0,0.26)] backdrop-blur transition hover:bg-white/14 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
							>
								<ChevronLeft className="size-5" />
							</button>

							<div className="pointer-events-auto flex items-center gap-2 rounded-full border border-white/14 bg-[#071325]/76 px-3 py-2 shadow-[0_12px_28px_rgba(0,0,0,0.22)] backdrop-blur">
								{heroSlides.map((slide, index) => (
									<button
										key={`${slide.id}-indicator`}
										type="button"
										aria-label={`Show hero slide ${index + 1}`}
										aria-current={
											activeSlide === index
												? "true"
												: undefined
										}
										onClick={() =>
											emblaApi?.scrollTo(index)
										}
										className={`h-2.5 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${
											activeSlide === index
												? "w-8 bg-secondary"
												: "w-2.5 bg-white/55 hover:bg-white"
										}`}
									/>
								))}
							</div>

							<button
								type="button"
								aria-label="Show next hero slide"
								onClick={showNextSlide}
								className="pointer-events-auto grid size-11 place-items-center rounded-full border border-white/18 bg-[#071325]/76 text-white shadow-[0_12px_28px_rgba(0,0,0,0.26)] backdrop-blur transition hover:bg-white/14 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
							>
								<ChevronRight className="size-5" />
							</button>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
